import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api, apiEndpoints } from '../../utils/apiService';

const StoreSelector: React.FC = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [allStores, setAllStores] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Initialize selected store from localStorage or user's default store
  useEffect(() => {
    const currentStoreId = localStorage.getItem('currentStoreId');

    if (currentStoreId) {
      setSelectedStoreId(parseInt(currentStoreId));
    } else if (user?.store_id) {
      setSelectedStoreId(user.store_id);
      // Set localStorage to match the user's default store
      localStorage.setItem('currentStoreId', user.store_id.toString());
    }
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    if (!isOpen) setSearch('');
  }, [isOpen]);

  const canSwitchStores = user?.is_platform_admin || user?.is_store_group_user;
  const storeChoices =
    user?.is_platform_admin && allStores.length
      ? allStores
      : user?.available_stores || [];

  useEffect(() => {
    if (!user?.is_platform_admin) return;

    const fetchAllStores = async () => {
      try {
        const response = await api.get(apiEndpoints.getAllStoresUnpaginated());
        if (response?.ok) {
          const data = await response.json();
          if (Array.isArray(data)) {
            setAllStores(data);
          } else if (data.results && Array.isArray(data.results)) {
            setAllStores(data.results);
          }
        }
      } catch (error) {
        console.error('Failed to fetch all stores for selector:', error);
      }
    };

    fetchAllStores();
  }, [user?.is_platform_admin]);

  // Only show when the user can switch stores and the backend provided options.
  if (!canSwitchStores || storeChoices.length === 0) {
    return null;
  }

  const handleStoreChange = (storeId: number) => {
    setSelectedStoreId(storeId);
    localStorage.setItem('currentStoreId', storeId.toString());
    setIsOpen(false);
    // Reload the page to fetch data for the new store
    window.location.reload();
  };

  const selectedStore = storeChoices.find(
    (store) => store.id === selectedStoreId,
  );
  const filteredStores = storeChoices.filter((store) =>
    search.trim()
      ? store.name.toLowerCase().includes(search.toLowerCase()) ||
        String(store.id).includes(search)
      : true,
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Store Selector Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="group flex min-w-[210px] items-center justify-between gap-2.5 rounded-xl border border-gray-200 bg-gradient-to-b from-white to-gray-50 px-3 py-2 text-left shadow-theme-xs transition-all hover:border-brand-200 hover:shadow-theme-md focus:outline-hidden focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:from-gray-800 dark:to-gray-900 dark:hover:border-brand-500/40"
        aria-label="Select store"
        aria-expanded={isOpen}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-8.5 w-8.5 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 ring-1 ring-brand-100 dark:bg-brand-500/10 dark:text-brand-300 dark:ring-brand-500/20">
            <svg
              className="h-4.5 w-4.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.8}
                d="M3 21h18M5 21V7.8a1 1 0 01.55-.89l6-3a1 1 0 01.9 0l6 3a1 1 0 01.55.89V21M9 10h.01M15 10h.01M9 14h.01M15 14h.01M10 21v-3.5a1.5 1.5 0 013 0V21"
              />
            </svg>
          </div>

          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-gray-900 dark:text-white/90">
              {selectedStore?.name || 'Select a store'}
            </div>
            <div className="truncate text-[11px] text-gray-500 dark:text-gray-400">
              {selectedStore
                ? `Store ID ${selectedStore.id}`
                : `${storeChoices.length} stores available`}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-gray-500 ring-1 ring-gray-200 transition-transform group-hover:text-brand-600 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700">
            <svg
              className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </span>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute left-0 right-0 z-50 mt-3 w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl shadow-gray-900/10 sm:left-auto sm:right-0 sm:w-[340px] dark:border-gray-700 dark:bg-gray-800">
          <div className="border-b border-gray-100 bg-gradient-to-r from-brand-50 via-white to-white px-4 py-3 dark:border-gray-700 dark:from-brand-500/10 dark:via-gray-800 dark:to-gray-800">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-700 dark:text-brand-300">
              Change store
            </p>
          </div>
          <div className="border-b border-gray-100 p-3 dark:border-gray-700">
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="m21 21-4.35-4.35M18 10.5a7.5 7.5 0 11-15 0 7.5 7.5 0 0115 0Z"
                  />
                </svg>
              </span>
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by store name or ID"
                className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-9 pr-3 text-sm text-gray-700 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-2 focus:ring-brand-500/10 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:placeholder:text-gray-500"
              />
            </div>
          </div>
          <div className="max-h-72 space-y-1 overflow-y-auto p-2">
            {filteredStores.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                No stores found
              </div>
            ) : null}
            {filteredStores.map((store) => (
              <button
                type="button"
                key={store.id}
                onClick={() => handleStoreChange(store.id)}
                className={`w-full rounded-xl px-3 py-3 text-left text-sm transition-colors ${
                  selectedStoreId === store.id
                    ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-100 dark:bg-brand-900/20 dark:text-brand-300 dark:ring-brand-500/20'
                    : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/60'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate font-semibold">{store.name}</div>
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Store ID {store.id}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {selectedStoreId === store.id ? (
                      <span className="rounded-full bg-white/80 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-brand-700 dark:bg-brand-950/40 dark:text-brand-300">
                        Active
                      </span>
                    ) : null}
                    <svg
                      className={`h-4 w-4 ${
                        selectedStoreId === store.id
                          ? 'text-brand-600 dark:text-brand-400'
                          : 'text-gray-300 dark:text-gray-600'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="m9 6 6 6-6 6"
                      />
                    </svg>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreSelector;
